import { scopeToNotificationSchool } from "@/lib/deep-link-scope";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { installNativeLifecycle } from "@/lib/native-lifecycle";
import { installNativePushHandlers, registerNativePush } from "@/lib/native-push";
import { requestInitialPermissions } from "@/lib/native-permissions";
import { isNative } from "@/lib/platform";
import { recordAppLaunch } from "@/lib/app-review";
import { useAuthStore } from "@/store/auth-store";
import { useAutoRegisterWebPush } from "@/hooks/use-web-push";

/**
 * Mounts once inside the Router. Wires up everything that needs the
 * native bridge AND the React-Router `navigate` function:
 *
 *   • App lifecycle (foreground/background, back button, deep links,
 *     status bar, splash dismissal, keyboard, network).
 *   • Push notification handlers, fanning taps into `navigate(path)`.
 *   • One-shot push registration the first time we see an authenticated
 *     user — guarded so we don't badger users with the OS prompt before
 *     login.
 *
 * No-ops on web (every helper internally short-circuits via `isNative()`),
 * so it's safe to render unconditionally.
 */
export function NativeLifecycle() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isSessionRestored = useAuthStore((s) => s.isSessionRestored);

  // CAMP-2026-0449 — the install effect must run EXACTLY ONCE for the app's
  // lifetime. It used to depend on `navigate`, and `installNativeLifecycle` is
  // async: if the identity of `navigate` ever changed, the effect re-ran and the
  // new `App.addListener("backButton")` could land before the previous run's
  // teardown resolved, leaving two live listeners. Two listeners means one back
  // press fires `navigate(-1)` twice — which pops two routes and looks exactly
  // like "back jumped straight to the Dashboard". Holding `navigate` in a ref
  // and using an empty dep list removes the whole race.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isNative()) return;
    // Count this cold start for the store-rating policy: one synchronous
    // localStorage write, no request (lib/app-review.ts).
    recordAppLaunch();

    let cancelled = false;
    let lifecycleCleanup: (() => void) | undefined;
    let pushCleanup: (() => void) | undefined;

    const open = (path: string, schoolId?: string) => {
      scopeToNotificationSchool(schoolId);
      try {
        navigateRef.current(path);
      } catch {
        // Falling back to a hard navigation guarantees the user lands
        // somewhere sensible even if React Router rejects the path.
        window.location.href = path;
      }
    };

    const pop = (delta: number) => navigateRef.current(delta);

    void (async () => {
      const lifecycle = await installNativeLifecycle({ navigate: pop, open });
      const push = await installNativePushHandlers({ onDeepLink: open });
      // Unmounted (or HMR-replaced) while we were awaiting — tear the freshly
      // installed listeners down immediately rather than leaking them.
      if (cancelled) {
        lifecycle();
        push();
        return;
      }
      lifecycleCleanup = lifecycle;
      pushCleanup = push;
    })();

    return () => {
      cancelled = true;
      try {
        lifecycleCleanup?.();
        pushCleanup?.();
      } catch {
        // ignore
      }
    };
  }, []);

  // Defer the OS push permission prompt until we actually have an
  // authenticated user — Apple/Google both penalise apps that prompt on
  // first launch ("dark pattern"). The promise return is intentionally
  // ignored; a denial just means no push for this session.
  useEffect(() => {
    if (!isNative() || !isAuthenticated || !isSessionRestored) return;
    // Ask for the app's runtime permissions up front, one prompt at a time:
    // notifications (push) first, then location, then camera. Point-of-use
    // guards (transport, face scan) re-prompt / guide if any are skipped here,
    // so a denial is never a dead end.
    void (async () => {
      await registerNativePush();
      await requestInitialPermissions();
    })();
  }, [isAuthenticated, isSessionRestored]);

  // Web/PWA push: hook is a no-op on native, on browsers without permission,
  // and on tabs that aren't a home-screen-installed PWA. Without this the
  // PushToken table was empty for every browser user and FCM had no targets.
  useAutoRegisterWebPush(isAuthenticated);

  return null;
}
